/**
 * Dispatch: one ongoing conversation for each of the account's PCs, from the
 * phone or any browser. A message there runs on that PC with what its desktop
 * app publishes (docs/dispatch.md).
 *
 *   /dispatch          opens the PC used last, or says there is none
 *   /dispatch/:id      that PC's thread: the chat page, with the PC in its header
 *
 * The thread is made the first time it is opened (`POST /devices/:id/chat`)
 * and handed to the chat page, which draws it as it draws any chat.
 */

import { useEffect, useState } from 'react';
import { ApiError, openDeviceChat, type Chat } from '../api';
import { DevicePicker } from '../components/DevicePicker';
import { MainHeader } from '../components/Shell';
import { Ph } from '../components/ui';
import { stashChat } from '../hooks/chatStash';
import { inDesktop } from '../desktop';
import { Link, useRouter } from '../router';
import { useApp } from '../state';
import { ChatPage } from './Chat';

const note = { padding: '40px 24px', textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' } as const;

/** The PC used last, or what to do with none. */
export function DispatchIndex() {
  const { devices, refreshDevices } = useApp();
  const { navigate } = useRouter();
  const first = devices?.[0];

  // A PC may have been published since the list was read.
  useEffect(() => {
    void refreshDevices();
  }, []);

  useEffect(() => {
    if (first) navigate(`/dispatch/${first.id}`, { replace: true });
  }, [first?.id]);

  return (
    <>
      <MainHeader />
      {devices && !first ? (
        <div style={{ ...note, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <Ph name="desktop" size={28} color="var(--text-faint)" />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: 'var(--text-strong)' }}>No PCs yet.</span>
          <span>
            {inDesktop()
              ? 'Switch on Dispatch in Settings → This computer, and this PC shows up here.'
              : 'Sign in to the Assist desktop app on a PC and it shows up here.'}
          </span>
        </div>
      ) : null}
    </>
  );
}

/** A PC's thread, opened. */
export function DispatchPage({ deviceId }: { deviceId: string }) {
  const { devices } = useApp();
  const [chat, setChat] = useState<Chat | null>(null);
  const [failed, setFailed] = useState<'missing' | 'failed' | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(null);
    openDeviceChat(deviceId)
      .then((c) => {
        if (cancelled) return;
        stashChat(c);
        setChat(c);
      })
      .catch((err: unknown) => {
        if (!cancelled) setFailed(err instanceof ApiError && err.status === 404 ? 'missing' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, attempt]);

  if (chat) return <ChatPage key={chat.id} id={chat.id} />;

  return (
    <>
      <MainHeader>{devices?.length ? <DevicePicker current={devices.find((d) => d.id === deviceId) ?? null} /> : null}</MainHeader>
      {failed === 'missing' ? (
        <div style={note}>
          That PC is not on this account. <Link to="/dispatch" style={{ color: 'var(--text-link)' }}>Open Dispatch</Link>
        </div>
      ) : failed === 'failed' ? (
        <div style={note}>
          This thread did not load.{' '}
          <button type="button" className="bare" onClick={() => setAttempt((n) => n + 1)} style={{ color: 'var(--text-link)' }}>
            Retry
          </button>
        </div>
      ) : null}
    </>
  );
}
